/**
 * Deep-link drawer orchestration for the dynamic exception route - turns
 * a `?exceptionObject=<object_id>` URL param into the row the drawer
 * renders.
 *
 * Cheap path first: the row may already be on the current page of
 * results, in which case no extra fetch is needed. Otherwise the central
 * drawer-detail endpoint resolves the full row for the tuple (the same
 * resolver the admin DQ Workbench drawer uses, so the two surfaces can't
 * drift). On-page rows that aren't tag-backed (inventory predicate
 * chips, no per-entry tag_id) are re-resolved through the same endpoint
 * so the drawer's sibling issues can expand to their Why/How.
 *
 * Composes the already-mocked `useExceptionDrawerDetail` seam - this
 * hook is NOT a new test-mock boundary. Extracted from
 * `DynamicExceptionRoute.tsx` as a pure structural move - state, memos
 * and the unresolvable-alert effect are byte-identical to the inline
 * originals.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CountKeyParts } from './useExceptionNoteCounts'
import { useExceptionDrawerDetail } from './useExceptionDrawerDetail'
import type { ExceptionRowIdentity } from '../config/exceptions/types'

export function useDrawerDeepLink({
  fpCode,
  exceptionCode,
  dataDomain,
  data,
  exceptionObject,
  setExceptionObject,
}: {
  fpCode?: string
  exceptionCode?: string
  dataDomain?: string
  data: Record<string, any>[]
  exceptionObject: string | null
  setExceptionObject: (next: string | null) => void
}) {
  const localRow = useMemo(() => {
    if (!exceptionObject) return null
    return (
      data.find(
        (r) =>
          String((r as ExceptionRowIdentity).object_id) === exceptionObject,
      ) ?? null
    )
  }, [data, exceptionObject])
  const drawerTuple = useMemo(
    () =>
      exceptionObject && dataDomain && exceptionCode
        ? {
            data_domain: dataDomain,
            exception_code: exceptionCode,
            object_id: exceptionObject,
          }
        : null,
    [exceptionObject, dataDomain, exceptionCode],
  )
  // On-page rows normally seed the drawer directly (no fetch). But inventory
  // list rows carry predicate chips (compute_product_row_errors) with no
  // per-entry tag_id, so the drawer's sibling issues can't expand to their
  // Why/How. When the clicked row isn't tag-backed, re-resolve through the
  // central detail endpoint - the tag-backed source admin + off-page already
  // use. Data-driven, not domain-hardcoded: any future predicate-chip domain
  // is covered automatically.
  const needsCentralResolve =
    !!localRow &&
    (
      (localRow.errors ?? []) as Array<{ tag_id?: number | null }>
    ).some((e) => e?.tag_id == null)
  const {
    row: fetchedRow,
    loading: drawerRowLoading,
    error: drawerDetailError,
  } = useExceptionDrawerDetail(
    fpCode,
    drawerTuple,
    !!exceptionObject && (localRow === null || needsCentralResolve),
  )
  // Seed with the on-page row for an instant open, then swap to the tag-backed
  // fetchedRow when it lands (the drawer's loading prop suppresses the skeleton
  // while a seed is present, so there's no flash for inventory).
  const drawerRow = needsCentralResolve
    ? fetchedRow ?? localRow
    : localRow ?? fetchedRow
  // Controlled-mode bridge for the note-dot. ``DataViewTable``
  // owns the ``useExceptionNoteCounts`` state internally; since the route
  // mounts the drawer itself (suppressing the table's internal one),
  // the drawer's ``onCountDelta`` has no path back to the table's
  // invalidate. The table calls ``onNoteCountsReady`` once after
  // mount with its stable ``invalidate`` fn; the route stashes it here and
  // wires it into the drawer.
  const noteCountInvalidateRef = useRef<
    ((parts: CountKeyParts, delta?: number) => void) | null
  >(null)
  const [unresolvableAlert, setUnresolvableAlert] = useState<string | null>(
    null,
  )
  useEffect(() => {
    // The endpoint is authoritative: it 404s only when the object is
    // genuinely unresolvable, surfacing as the hook's ``error``. Only
    // off-page rows depend on the fetch to populate the drawer - when an
    // on-page ``localRow`` is seeding it (including the inventory re-resolve),
    // a fetch error simply leaves the seeded row in place instead of closing
    // the drawer.
    if (!exceptionObject || !drawerDetailError || localRow) return
    setUnresolvableAlert(
      `That linked row (object ${exceptionObject}) is no longer available - it may have been resolved or the data has aged out.`,
    )
    setExceptionObject(null)
    // setExceptionObject is stable via useCallback; omit so the effect
    // only fires when its inputs flip, not on every URL write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerDetailError, exceptionObject, localRow])

  return {
    drawerRow,
    drawerRowLoading,
    localRow,
    unresolvableAlert,
    setUnresolvableAlert,
    noteCountInvalidateRef,
  }
}
